import { Divider, Grid, Radio, Slider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  AVOID_FEATURES,
  OptimizerConfig,
  TrafficOptions,
  TransportModes,
  TruckOptions,
  TunnelCategory,
} from '@nx-smartmonkey/shared/domain';
import { RoutalDialog, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import FormBoolean from '../../../../components/form-fields/FormBoolean';
import { getOptimizerConfigSmRootMap } from '../../../../constants/OptimizerConfig';
import {
  SHIPPED_HAZARDOUS_GOODS,
  TRAFFIC_MULTIPLIER_VALUES,
  TRANSPORT_MODE,
} from '../../../../constants/TrafficOptions';
import { useRetrieveActivePlan } from '../../../../hooks/plan/useRetrieveActivePlan';
import { useUpdateActivePlan } from '../../../../hooks/plan/useUpdateActivePlan';
import { useRetrieveActiveProjectResources } from '../../../../hooks/project/useRetrieveActiveProjectResources';
import { useAsUser } from '../../../../hooks/session/useAsUser';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import { Accordion } from '../../../../react-components/Accordion';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';
import { RenderOptimizerParameters } from '../../../shared/optimizer-config/OptimizerConfigComponent';

interface OptimizeDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OptimizerOptionsDialog = ({ isOpen, onClose }: OptimizeDialogProps) => {
  const intl = useIntl();
  const { asUser } = useAsUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { data: activePlan } = useRetrieveActivePlan();
  const { success: planUpdated, isLoading: updatePlanLoading, run: updateActivePlan } = useUpdateActivePlan();

  const [trafficOptions, setTrafficOptions] = useState<Record<string, any>>({
    multiplier: activePlan?.traffic_options?.multiplier,
    mode: activePlan?.traffic_options?.mode,
    departure_at: activePlan?.traffic_options?.departure_at,
    ...(activePlan?.traffic_options?.mode === `truck`
      ? {
          truck: {
            shipped_hazardous_goods: activePlan?.traffic_options?.truck?.shipped_hazardous_goods,
            gross_weight: activePlan?.traffic_options?.truck?.gross_weight,
            height: activePlan?.traffic_options?.truck?.height,
            width: activePlan?.traffic_options?.truck?.width,
            length: activePlan?.traffic_options?.truck?.length,
            tunnel_category: activePlan?.traffic_options?.truck?.tunnel_category,
            axle_count: activePlan?.traffic_options?.truck?.axle_count,
            type: activePlan?.traffic_options?.truck?.type,
            trailer_count: activePlan?.traffic_options?.truck?.trailer_count,
          },
        }
      : {}),
    avoid: activePlan?.traffic_options?.avoid,
    // departure_at: activePlan?.traffic_options?.departure_at,
  });

  const [optimizerConfig, setOptimizerConfig] = useState<Record<string, any>>({
    max_wait_time: activePlan?.optimizer_config?.max_wait_time,
    first_solution_strategy: activePlan?.optimizer_config?.first_solution_strategy,
    local_search_strategy: activePlan?.optimizer_config?.local_search_strategy,
    time_limit_seconds: activePlan?.optimizer_config?.time_limit_seconds,
    lns_time_limit: activePlan?.optimizer_config?.lns_time_limit,
    balance_services: activePlan?.optimizer_config?.balance_services,
    service_duration: activePlan?.optimizer_config?.service_duration,
    operation_country: activePlan?.optimizer_config?.operation_country,
    time_span_coefficient: activePlan?.optimizer_config?.time_span_coefficient,
    distance_span_coefficient: activePlan?.optimizer_config?.distance_span_coefficient,
    use_distance: activePlan?.optimizer_config?.use_distance,
    drop_penalty: activePlan?.optimizer_config?.drop_penalty,
    distance_balance: activePlan?.optimizer_config?.distance_balance,
    isolation_factor: activePlan?.optimizer_config?.isolation_factor,
  });

  useEffect(() => {
    if (activePlan?.optimizer_config) {
      setOptimizerConfig({
        max_wait_time: activePlan?.optimizer_config?.max_wait_time,
        first_solution_strategy: activePlan?.optimizer_config?.first_solution_strategy,
        local_search_strategy: activePlan?.optimizer_config?.local_search_strategy,
        time_limit_seconds: activePlan?.optimizer_config?.time_limit_seconds,
        lns_time_limit: activePlan?.optimizer_config?.lns_time_limit,
        balance_services: activePlan?.optimizer_config?.balance_services,
        service_duration: activePlan?.optimizer_config?.service_duration,
        operation_country: activePlan?.optimizer_config?.operation_country,
        time_span_coefficient: activePlan?.optimizer_config?.time_span_coefficient,
        distance_span_coefficient: activePlan?.optimizer_config?.distance_span_coefficient,
        use_distance: activePlan?.optimizer_config?.use_distance,
        drop_penalty: activePlan?.optimizer_config?.drop_penalty,
        distance_balance: activePlan?.optimizer_config?.distance_balance,
        isolation_factor: activePlan?.optimizer_config?.isolation_factor,
      });
    }
    if (activePlan?.traffic_options) {
      setTrafficOptions({
        multiplier: activePlan?.traffic_options?.multiplier,
        mode: activePlan?.traffic_options?.mode,
        departure_at: activePlan?.traffic_options?.departure_at,
        ...(activePlan?.traffic_options?.mode === `truck`
          ? {
              truck: {
                shipped_hazardous_goods: activePlan?.traffic_options?.truck?.shipped_hazardous_goods,
                gross_weight: activePlan?.traffic_options?.truck?.gross_weight,
                height: activePlan?.traffic_options?.truck?.height,
                width: activePlan?.traffic_options?.truck?.width,
                length: activePlan?.traffic_options?.truck?.length,
                tunnel_category: activePlan?.traffic_options?.truck?.tunnel_category,
                axle_count: activePlan?.traffic_options?.truck?.axle_count,
                type: activePlan?.traffic_options?.truck?.type,
                trailer_count: activePlan?.traffic_options?.truck?.trailer_count,
              },
            }
          : {}),
        avoid: activePlan?.traffic_options?.avoid,
        // departure_at: activePlan?.traffic_options?.departure_at,
      });
    }
  }, [activePlan]);

  useEffect(() => {
    if (planUpdated) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planUpdated]);

  const getMultiplierHumanValue = (value?: number) => {
    if (value) {
      const regularValue = TRAFFIC_MULTIPLIER_VALUES.find((val) => val.value === value);
      if (regularValue) {
        return <Text style={{ width: `100%`, textAlign: `start` }} intlId={regularValue.labelId} />;
      }
      return <Text style={{ width: `100%`, textAlign: `start` }}>{value}</Text>;
    }
    return <Text style={{ width: `100%`, textAlign: `start` }} intlId="not_selected" />;
  };

  return (
    <RoutalDialog
      open={isOpen || updatePlanLoading}
      titleContent={<Text variant="h6" intlId="planner.plan_details.header.optimizer_config.title" />}
      cancelButtonText={intl.formatMessage({ id: `cancel` })}
      confirmButtonText={intl.formatMessage({ id: `confirm` })}
      onCloseDialog={onClose}
      onConfirm={() => {
        updateActivePlan({
          ...(optimizerConfig ? { optimizer_config: OptimizerConfig.create(optimizerConfig) } : {}),
          ...(trafficOptions
            ? {
                traffic_options: TrafficOptions.create({
                  ...trafficOptions,
                  ...(trafficOptions.truck ? { truck: TruckOptions.create(trafficOptions.truck) } : {}),
                }),
              }
            : {}),
        });
      }}
      contentStyles={{
        padding: `8px 0`,
        paddingBottom: `16px`,
        ...(updatePlanLoading
          ? {
              pointerEvents: `none`,
              opacity: 0.5,
            }
          : {}),
      }}
      showLoader={false}
      loading={updatePlanLoading}
    >
      {optimizerConfig ? (
        <Grid container direction="column" wrap="nowrap" style={{ padding: `0 24px` }}>
          <Text variant="body-large" isBold intlId="planner.plan_details.header.optimizer_config.optimizer_options" />

          <VSpacer medium />
          <Grid item>
            <Text
              variant="body"
              isBold
              key="label-distance_balance"
              intlId={`optimizeroptions.details.distance_balance`}
            />
            <Text
              variant="label"
              key="label-distance_balance-descr"
              intlId={`optimizeroptions.details.distance_balance.description`}
            />

            <Grid container justifyContent={`center`}>
              <Slider
                style={{ width: `50%` }}
                defaultValue={optimizerConfig.distance_balance}
                step={1}
                min={0.1}
                max={4}
                marks={[
                  { value: 0.1, label: intl.formatMessage({ id: `optimizeroptions.details.distance_balance.min` }) },
                  { value: 1, label: `` },
                  { value: 2, label: `` },
                  { value: 3, label: `` },
                  { value: 4, label: intl.formatMessage({ id: `optimizeroptions.details.distance_balance.max` }) },
                ]}
                onChange={(e, value) => {
                  setOptimizerConfig({ ...optimizerConfig, distance_balance: value });
                }}
              />
            </Grid>
          </Grid>

          <VSpacer small />
          <Grid item>
            <Text
              variant="body"
              isBold
              key="label-isolation_factor"
              intlId={`optimizeroptions.details.isolation_factor`}
            />
            <Text
              variant="label"
              key="label-isolation_factor-descr"
              intlId={`optimizeroptions.details.isolation_factor.description`}
            />

            <Grid container justifyContent={`center`}>
              <Slider
                style={{ width: `50%` }}
                defaultValue={optimizerConfig.isolation_factor}
                step={0.5}
                min={0}
                max={2}
                marks={[
                  { value: 0, label: intl.formatMessage({ id: `optimizeroptions.details.isolation_factor.min` }) },
                  { value: 0.5, label: `` },
                  { value: 1, label: `` },
                  { value: 1.5, label: `` },
                  { value: 2, label: intl.formatMessage({ id: `optimizeroptions.details.isolation_factor.max` }) },
                ]}
                onChange={(e, value) => {
                  setOptimizerConfig({ ...optimizerConfig, isolation_factor: value });
                }}
              />
            </Grid>
          </Grid>

          <VSpacer small />

          <Grid item>
            <Text variant="body" isBold key="label-max_wait_time" intlId={`optimizeroptions.details.max_wait_time`} />
            <Text
              variant="label"
              key="label-max_wait_time-descr"
              intlId={`optimizeroptions.details.max_wait_time.description`}
            />

            <InputText
              key="max_wait_time"
              inputContainerStyles={{
                width: `150px`,
              }}
              inputProps={{
                min: 0,
              }}
              variant="outlined"
              type="integer"
              fieldValue={optimizerConfig.max_wait_time ? optimizerConfig.max_wait_time / 60 : undefined}
              handleEnter={(value: any) => {
                setOptimizerConfig({
                  ...optimizerConfig,
                  max_wait_time: value * 60,
                });
              }}
              onChange={(value: any) => {
                setOptimizerConfig({
                  ...optimizerConfig,
                  max_wait_time: value * 60,
                });
              }}
              suffix="min"
            />
          </Grid>

          <VSpacer small />

          <Grid item>
            <Text
              variant="body"
              isBold
              key="label-balance_services"
              intlId={`optimizeroptions.details.balance_services`}
            />
            <Text
              variant="label"
              key="label-balance_services-descr"
              intlId={`optimizeroptions.details.balance_services.description`}
            />

            <FormBoolean
              key="balance_services"
              checked={optimizerConfig.balance_services}
              onChange={(event: any) => {
                const { checked } = event.target;
                setOptimizerConfig({
                  ...optimizerConfig,
                  balance_services: checked,
                });
              }}
            />
          </Grid>

          <VSpacer small />

          <Grid item>
            <Text
              variant="body"
              isBold
              key="label-time_span_coefficient"
              intlId={`optimizeroptions.details.time_span_coefficient`}
            />
            <Text
              variant="label"
              key="label-time_span_coefficient-descr"
              intlId={`optimizeroptions.details.time_span_coefficient.description`}
            />

            <FormBoolean
              key="balance_services"
              checked={optimizerConfig.time_span_coefficient !== 0}
              onChange={(event: any) => {
                const { checked } = event.target;
                setOptimizerConfig({
                  ...optimizerConfig,
                  time_span_coefficient: checked ? 1 : 0,
                });
              }}
            />
          </Grid>

          <VSpacer small />

          <Grid item>
            <Text
              variant="body"
              isBold
              key="label-distance_span_coefficient"
              intlId={`optimizeroptions.details.distance_span_coefficient`}
            />
            <Text
              variant="label"
              key="label-distance_span_coefficient-descr"
              intlId={`optimizeroptions.details.distance_span_coefficient.description`}
            />

            <FormBoolean
              key="balance_services"
              checked={optimizerConfig.distance_span_coefficient !== 0}
              onChange={(event: any) => {
                const { checked } = event.target;
                setOptimizerConfig({
                  ...optimizerConfig,
                  distance_span_coefficient: checked ? 1 : 0,
                });
              }}
            />
          </Grid>

          <VSpacer small />

          {userCredentials?.isStaff(asUser) ? (
            <div
              style={{
                border: `1px solid ${RoutalPalette.warning.medium}`,
                padding: `8px`,
                borderRadius: `2px`,
              }}
            >
              <Accordion
                id="optimizer-options-admin"
                expandIconStyles={{
                  color: RoutalPalette.text,
                  position: `absolute`,
                  top: -4,
                  right: -6,
                }}
                headerStyles={{
                  position: `relative`,
                  padding: `0`,
                }}
                headerContent={<Text style={{ cursor: `pointer` }} intlId="optimizeroptions.details.admin.title" />}
                contentStyle={{
                  flex: 1,
                  width: `380px`,
                  paddingTop: `16px`,
                }}
              >
                <RenderOptimizerParameters
                  isAdmin
                  onChange={(updatedData: OptimizerConfig) => {
                    setOptimizerConfig({ ...updatedData.props });
                  }}
                  optimizerConfig={OptimizerConfig.create(optimizerConfig)}
                  optimizerConfigMap={getOptimizerConfigSmRootMap()}
                />
              </Accordion>
            </div>
          ) : null}
        </Grid>
      ) : null}

      <VSpacer medium />

      <Divider />

      <VSpacer large />

      <Grid container direction="column" wrap="nowrap" style={{ padding: `0 24px` }}>
        <Text variant="body-large" isBold intlId="planner.plan_details.header.optimizer_config.traffic_options" />

        <VSpacer medium />

        {!projectResources?.traffic ? (
          <Grid item key="traffic-options-multiplier" style={{ width: `380px` }}>
            <Text key="traffic_options.details.multiplier" intlId="traffic_options.details.multiplier" />

            <RoutalSelect
              id="select-multiplier"
              popoverProps={{
                options: TRAFFIC_MULTIPLIER_VALUES.map(
                  (option: any): RoutalSelectMultiSelectOption => ({
                    key: `multiplier-menuItem${option.value}`,
                    content: <Text intlId={`${option.labelId}`} style={{ cursor: `pointer` }} />,
                    onClick: () => {
                      setTrafficOptions({
                        ...trafficOptions,
                        multiplier: option.value,
                      });
                    },
                  })
                ),
              }}
            >
              <Text style={{ width: `100%`, cursor: `pointer`, textAlign: `start` }}>
                {getMultiplierHumanValue(trafficOptions.multiplier)}
              </Text>
            </RoutalSelect>
            <VSpacer medium />
          </Grid>
        ) : (
          <>
            <Grid item key="traffic-options-departure_at">
              <Text key="traffic_options.details.departure_at" intlId="traffic_options.details.departure_at" />
              <Grid container direction="row" alignItems="center">
                <Radio
                  checked={!trafficOptions.departure_at || trafficOptions.departure_at === `now`}
                  onChange={() => {
                    setTrafficOptions({ ...trafficOptions, departure_at: `now` });
                  }}
                />
                <Text
                  key="traffic_options.details.departure_at.now"
                  intlId="traffic_options.details.departure_at.now"
                />
                <Radio
                  checked={trafficOptions.departure_at && trafficOptions.departure_at !== `now`}
                  onChange={() => {
                    setTrafficOptions({ ...trafficOptions, departure_at: new Date() });
                  }}
                />
                <Text
                  key="traffic_options.details.departure_at.date"
                  intlId="traffic_options.details.departure_at.date"
                />
              </Grid>

              <VSpacer small />

              {trafficOptions.departure_at && trafficOptions.departure_at !== `now` && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={dayjs(trafficOptions.departure_at)}
                    onChange={(newValue) => setTrafficOptions({ ...trafficOptions, departure_at: newValue })}
                  />
                </LocalizationProvider>
              )}
              <VSpacer small />
            </Grid>

            <Grid item key="traffic-options-avoid-features" style={{ width: `380px` }}>
              <Text key="traffic_options.details.avoid.features" intlId="traffic_options.details.avoid.features" />

              <VSpacer small />

              <RoutalSelect
                id="select-avoid"
                popoverProps={{
                  options: [
                    ...AVOID_FEATURES.map(
                      (option): RoutalSelectMultiSelectOption => ({
                        key: `avoid-menuItem${option}`,
                        checked: (trafficOptions?.avoid?.features ?? []).includes(option),
                        hideCheckIcon: false,
                        closeOnClick: false,
                        content: <Text intlId={`${option}`} style={{ cursor: `pointer` }} />,
                        onClick: () => {
                          let avoidFeatures = [];

                          if ((trafficOptions?.avoid?.features ?? []).includes(option)) {
                            avoidFeatures = (trafficOptions?.avoid?.features ?? []).filter(
                              (key: string) => key !== option
                            );
                          } else {
                            avoidFeatures = (trafficOptions?.avoid?.features ?? []).concat(option);
                          }

                          setTrafficOptions({
                            ...trafficOptions,
                            avoid: {
                              features: avoidFeatures,
                            },
                          });
                        },
                      })
                    ),
                  ],
                }}
              >
                <Text
                  style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}
                  intlId="traffic_options.details.avoid.features.num_selected"
                  intlValues={{ num: (trafficOptions?.avoid?.features ?? []).length }}
                />
              </RoutalSelect>

              <VSpacer medium />
            </Grid>

            <Grid item key="traffic-options-mode" style={{ width: `380px` }}>
              <Text key="traffic_options.details.mode" intlId="traffic_options.details.mode" />

              <VSpacer small />

              <RoutalSelect
                id="select-mode"
                popoverProps={{
                  options: [
                    ...TRANSPORT_MODE.map(
                      (option): RoutalSelectMultiSelectOption => ({
                        key: `mode-menuItem${option.value}`,
                        content: <Text intlId={`${option.labelId}`} style={{ cursor: `pointer` }} />,
                        onClick: () => {
                          const mode = option.value as TransportModes;

                          setTrafficOptions({
                            ...trafficOptions,
                            mode,
                            ...(mode === `truck`
                              ? {
                                  truck: {
                                    shipped_hazardous_goods:
                                      activePlan?.traffic_options?.truck?.shipped_hazardous_goods,
                                    gross_weight: activePlan?.traffic_options?.truck?.gross_weight,
                                    height: activePlan?.traffic_options?.truck?.height,
                                    width: activePlan?.traffic_options?.truck?.width,
                                    length: activePlan?.traffic_options?.truck?.length,
                                    tunnel_category: activePlan?.traffic_options?.truck?.tunnel_category,
                                    axle_count: activePlan?.traffic_options?.truck?.axle_count,
                                    type: activePlan?.traffic_options?.truck?.type,
                                    trailer_count: activePlan?.traffic_options?.truck?.trailer_count,
                                  },
                                }
                              : {}),
                          });
                        },
                      })
                    ),
                  ],
                }}
              >
                <Text
                  style={{ width: `100%`, cursor: `pointer`, textAlign: `start` }}
                  intlId={`traffic_options.details.mode.${trafficOptions.mode ?? `car`}`}
                />
              </RoutalSelect>
              <VSpacer medium />
            </Grid>

            {trafficOptions.mode === `truck` ? (
              <>
                <Grid item key="traffic-options-shipped_hazardous_goods" style={{ width: `380px` }}>
                  <Text
                    key="traffic_options.details.truck.shipped_hazardous_goods"
                    intlId="traffic_options.details.truck.shipped_hazardous_goods"
                  />

                  <VSpacer small />

                  <RoutalSelect
                    id="select-shipped_hazardous_goods"
                    popoverProps={{
                      options: [
                        ...SHIPPED_HAZARDOUS_GOODS.map(
                          (option): RoutalSelectMultiSelectOption => ({
                            key: `shipped_hazardous_goods-menuItem${option.value}`,
                            checked: trafficOptions.truck!.shipped_hazardous_goods?.includes(option.value),
                            hideCheckIcon: false,
                            closeOnClick: false,
                            content: <Text intlId={`${option.labelId}`} style={{ cursor: `pointer` }} />,
                            onClick: (e: any, value: any) => {
                              let goods = [];

                              if ((trafficOptions.truck!.shipped_hazardous_goods ?? []).includes(option.value)) {
                                goods = (trafficOptions.truck!.shipped_hazardous_goods ?? []).filter(
                                  (key: string) => key !== option.value
                                );
                              } else {
                                goods = (trafficOptions.truck!.shipped_hazardous_goods ?? []).concat(option.value);
                              }

                              setTrafficOptions({
                                ...trafficOptions,
                                truck: {
                                  ...trafficOptions.truck,
                                  shipped_hazardous_goods: goods,
                                },
                              });
                            },
                          })
                        ),
                      ],
                    }}
                  >
                    <Text
                      style={{ width: `100%`, cursor: `pointer`, textAlign: `start` }}
                      intlId="traffic_options.details.truck.shipped_hazardous_goods.num_selected"
                      intlValues={{ num: (trafficOptions.truck!.shipped_hazardous_goods ?? []).length }}
                    />
                  </RoutalSelect>
                  <VSpacer medium />
                </Grid>

                <Grid item key="traffic_options-truck-gross_weight" style={{ width: `380px` }}>
                  <Text
                    key="traffic_options.details.truck.gross_weight"
                    intlId="traffic_options.details.truck.gross_weight"
                  />

                  <VSpacer small />

                  <InputText
                    key="number-truck-gross_weight"
                    inputContainerStyles={{
                      width: `100%`,
                      backgroundColor: `white`,
                    }}
                    inputStyles={{
                      color: `#393939`,
                    }}
                    variant="outlined"
                    type="number"
                    fieldValue={trafficOptions.truck!.gross_weight}
                    handleEnter={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          gross_weight: value,
                        },
                      });
                    }}
                    onChange={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          gross_weight: value,
                        },
                      });
                    }}
                    suffix="kg"
                  />
                  <VSpacer medium />
                </Grid>

                <Grid item key="traffic_options-truck-height" style={{ width: `380px` }}>
                  <Text key="traffic_options.details.truck.height" intlId="traffic_options.details.truck.height" />

                  <VSpacer small />

                  <InputText
                    key="number-truck-height"
                    inputContainerStyles={{
                      width: `100%`,
                      backgroundColor: `white`,
                    }}
                    inputStyles={{
                      color: `#393939`,
                    }}
                    variant="outlined"
                    type="number"
                    fieldValue={trafficOptions.truck!.height}
                    handleEnter={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          height: value,
                        },
                      });
                    }}
                    onChange={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          height: value,
                        },
                      });
                    }}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    suffix="cm"
                  />
                  <VSpacer medium />
                </Grid>

                <Grid item key="traffic_options-truck-width" style={{ width: `380px` }}>
                  <Text key="traffic_options.details.truck.width" intlId="traffic_options.details.truck.width" />

                  <VSpacer small />

                  <InputText
                    key="number-truck-width"
                    inputContainerStyles={{
                      width: `100%`,
                      backgroundColor: `white`,
                    }}
                    inputStyles={{
                      color: `#393939`,
                    }}
                    variant="outlined"
                    type="number"
                    fieldValue={trafficOptions.truck!.width}
                    handleEnter={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          width: value,
                        },
                      });
                    }}
                    onChange={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          width: value,
                        },
                      });
                    }}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    suffix="cm"
                  />
                  <VSpacer medium />
                </Grid>

                <Grid item key="traffic_options-truck-length" style={{ width: `380px` }}>
                  <Text key="traffic_options.details.truck.length" intlId="traffic_options.details.truck.length" />

                  <VSpacer small />

                  <InputText
                    key="number-truck-length"
                    inputContainerStyles={{
                      width: `100%`,
                      backgroundColor: `white`,
                    }}
                    inputStyles={{
                      color: `#393939`,
                    }}
                    variant="outlined"
                    type="number"
                    fieldValue={trafficOptions.truck!.length}
                    handleEnter={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          length: value,
                        },
                      });
                    }}
                    onChange={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          length: value,
                        },
                      });
                    }}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    suffix="cm"
                  />
                  <VSpacer medium />
                </Grid>

                {/* tunnel_category */}
                <Grid item key="traffic-options-tunnel_category" style={{ width: `380px` }}>
                  <Text
                    key="traffic_options.details.truck.tunnel_category"
                    intlId="traffic_options.details.truck.tunnel_category"
                  />

                  <VSpacer small />

                  <RoutalSelect
                    id="select-tunnel_category"
                    popoverProps={{
                      options: [
                        ...[`B`, `C`, `D`, `E`].map(
                          (option): RoutalSelectMultiSelectOption => ({
                            key: `tunnel_category-menuItem${option}`,
                            content: <Text style={{ cursor: `pointer` }}>{option}</Text>,
                            onClick: () => {
                              setTrafficOptions({
                                ...trafficOptions,
                                truck: {
                                  ...trafficOptions.truck,
                                  tunnel_category: option as TunnelCategory,
                                },
                              });
                            },
                          })
                        ),
                      ],
                    }}
                  >
                    {trafficOptions.truck!.tunnel_category ? (
                      <Text style={{ width: `100%`, cursor: `pointer`, textAlign: `start` }}>
                        {trafficOptions.truck!.tunnel_category}
                      </Text>
                    ) : (
                      <Text style={{ width: `100%`, cursor: `pointer`, textAlign: `start` }} intlId="not_selected" />
                    )}
                  </RoutalSelect>
                  <VSpacer medium />
                </Grid>

                <Grid item key="traffic_options-truck-axle_count" style={{ width: `380px` }}>
                  <Text
                    key="traffic_options.details.truck.axle_count"
                    intlId="traffic_options.details.truck.axle_count"
                  />

                  <VSpacer small />

                  <InputText
                    key="number-truck-axle_count"
                    inputContainerStyles={{
                      width: `100%`,
                      backgroundColor: `white`,
                    }}
                    inputStyles={{
                      color: `#393939`,
                    }}
                    variant="outlined"
                    type="number"
                    fieldValue={trafficOptions.truck!.axle_count}
                    handleEnter={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          axle_count: value,
                        },
                      });
                    }}
                    onChange={(value: any) => {
                      setTrafficOptions({
                        ...trafficOptions,
                        truck: {
                          ...trafficOptions.truck,
                          axle_count: value,
                        },
                      });
                    }}
                    placeholderIntlId="traffic_options.details.truck.axle_count_ph"
                  />
                  <VSpacer medium />
                </Grid>
              </>
            ) : null}
          </>
        )}

        <VSpacer small />

        {userCredentials?.isStaff(asUser) ? (
          <div
            style={{
              border: `1px solid ${RoutalPalette.warning.medium}`,
              padding: `8px`,
              borderRadius: `2px`,
            }}
          >
            <Accordion
              id="traffic-options-admin"
              expandIconStyles={{
                color: RoutalPalette.text,
                position: `absolute`,
                top: -4,
                right: -6,
              }}
              headerStyles={{
                position: `relative`,
                padding: `0`,
              }}
              headerContent={<Text style={{ cursor: `pointer` }} intlId="traffic_options.details.admin.title" />}
              contentStyle={{
                flex: 1,
                width: `380px`,
                paddingTop: `16px`,
              }}
            >
              <Grid item key="traffic_options-multiplier">
                <Text key="traffic_options.details.multiplier" intlId="traffic_options.details.multiplier" />

                <InputText
                  key="number-truck-multiplier"
                  inputContainerStyles={{
                    width: `100%`,
                    backgroundColor: `white`,
                  }}
                  inputStyles={{
                    color: `#393939`,
                  }}
                  variant="outlined"
                  type="number"
                  fieldValue={trafficOptions.multiplier}
                  handleEnter={(value: any) => {
                    setTrafficOptions({
                      ...trafficOptions,
                      multiplier: value,
                    });
                  }}
                  onChange={(value: any) => {
                    setTrafficOptions({
                      ...trafficOptions,
                      multiplier: value,
                    });
                  }}
                />

                <VSpacer small />
              </Grid>
            </Accordion>
          </div>
        ) : null}
      </Grid>
    </RoutalDialog>
  );
};
